import * as React from 'react';
import type { OptionProps } from 'react-apollo';
import { graphql } from 'react-apollo';

import classNames from 'classnames';
import gql from 'graphql-tag';
import type Q from 'q';
import { compose, withProps } from 'recompose';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import cookie from 'js/lib/cookie';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';

import { Box, CenterBox, StyleSheet, color, css, transition } from '@coursera/coursera-ui';
import { SvgExternalLink } from '@coursera/coursera-ui/svg';

import ThirdPartyAuthButtons from 'bundles/authentication/shared/components/third-party-auth/ThirdPartyAuthButtons';
import { isTermsOfUseRolloutForUS } from 'bundles/authentication/utils';
import { isArkoseBotManagerForSignupEnabled } from 'bundles/authentication/utils/experimentUtils';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import { AUTH_MODAL_LOGO_SVG } from 'bundles/front-page/constants/defaults';
import type ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import CourseraLogo from 'bundles/page/components/CourseraLogo';
import { TrackedA, TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import requestCountryCode from 'bundles/phoenix/template/models/requestCountryCode';
import * as JWTLoginAPIUtils from 'bundles/saml-sso/utils/JWTLoginAPIUtils';
import type {
  COMPLETE_FORM_KEY_TYPE,
  LINK_FORM_KEY_TYPE,
  LOGIN_FORM_KEY_TYPE,
  SIGNUP_FORM_KEY_TYPE,
} from 'bundles/user-account/common/constants';
import {
  COMPLETE_FORM_KEY,
  LINK_FORM_KEY,
  LOGIN_FORM_KEY,
  SIGNUP_FORM_KEY,
  getFormTranslations,
} from 'bundles/user-account/common/constants';
import AuthenticationModalContentV1 from 'bundles/user-account/components/AuthenticationModalContentV1';
import CompleteForm from 'bundles/user-account/components/CompleteForm';
import LinkedAccountForm from 'bundles/user-account/components/LinkedAccountForm';
import LoginForm from 'bundles/user-account/components/LoginForm';
import LoginLink from 'bundles/user-account/components/LoginLink';
import SignupForm from 'bundles/user-account/components/SignupForm';
import SignupLink from 'bundles/user-account/components/SignupLink';
import SsoButton from 'bundles/user-account/components/SsoButton';
import ForceSSOLogin from 'bundles/user-account/queries/ForceSSOLogin.graphql';
import type {
  ForceSsoLoginQuery,
  ForceSsoLoginQueryVariables,
} from 'bundles/user-account/queries/__generated__/ForceSSOLogin';
import { getEnabledVersions } from 'bundles/user-account/utils/ssoUtils';
import { SHOW_TERMS_OF_USE_ACCEPT_PARAM } from 'bundles/user-consent/constants';

import _t from 'i18n!nls/user-account';

import 'css!./__styles__/AuthenticationModal';

const styles = StyleSheet.create({
  transitionContainer: {
    transition: `height 0.3s ${transition.easeOutFunction}`,
    overflowY: 'auto',
  },
  courseraRebrandLogoImage: {
    width: 'auto',
    height: 85,
  },
});

const modeToForm = {
  [COMPLETE_FORM_KEY]: CompleteForm,
  [LOGIN_FORM_KEY]: LoginForm,
  [SIGNUP_FORM_KEY]: SignupForm,
  [LINK_FORM_KEY]: LinkedAccountForm,
};

type State = {
  useJWT: boolean;
  activeForm: SIGNUP_FORM_KEY_TYPE | LOGIN_FORM_KEY_TYPE | COMPLETE_FORM_KEY_TYPE | LINK_FORM_KEY_TYPE;
};

type Query = {
  organization: string;
  webToken?: string;
  invitationToken?: string;
};

type ThirdPartyOrgsGraphqlData = {
  organizations: Array<ThirdPartyOrganizationsV1>;
};

type PropsFromForceSsoLoginQuery = {
  forceSSOLogin?: boolean;
};

type Props = {
  switchFormsByQueryParam?: boolean;
  authMode: SIGNUP_FORM_KEY_TYPE | LOGIN_FORM_KEY_TYPE | COMPLETE_FORM_KEY_TYPE | LINK_FORM_KEY_TYPE;
  hideNav?: boolean;
  router: InjectedRouter;
  isEnterprise?: boolean;
  organizations: [ThirdPartyOrganizationsV1];
  inModal?: boolean;
  hideLogo?: boolean;
  customRedirectUrl?: string;
  overrideFacebookRedirectBlock?: boolean;
  programId?: string;
} & PropsFromForceSsoLoginQuery;

class AuthenticationFormsContainer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { authMode } = this.props;

    // in modal authMode changes via link and is static, outside of a modal form is swapped via state
    this.state = {
      useJWT: true,
      activeForm: authMode,
    };
  }

  componentDidMount() {
    const {
      router: {
        location: { query },
      },
    } = this.props;
    let newState = {};
    if (query.webToken) {
      this.handleJWTInviteCheck(query as Query);
    } else {
      newState = { ...newState, useJWT: false };
    }
    // remove fragment meta tag if it's CSR behind log-in page.
    const fragmentMetaTag = document.querySelector('meta[name="fragment"]');
    if (fragmentMetaTag && fragmentMetaTag.parentNode) {
      fragmentMetaTag.parentNode.removeChild(fragmentMetaTag);
    }

    this.setState(() => newState);
  }

  componentWillReceiveProps(nextProps: Props) {
    const { authMode } = this.props;

    if (nextProps.authMode !== authMode) {
      this.setState(() => ({
        activeForm: nextProps.authMode,
      }));
    }
  }

  handleJWTInviteCheck(query: Query) {
    const organizationSlug = query.organization;
    const { webToken, invitationToken } = query;
    const { router } = this.props;
    const onFail = () => {
      const newQueryParams = { ...(router.location.query as Query), errorCode: 'invalidWebToken' };
      newQueryParams.webToken = undefined;
      router.push({
        pathname: router.location.pathname,
        params: router.params,
        query: newQueryParams,
      });
      this.setState(() => ({ useJWT: false }));
    };
    let promise: Q.Promise<{ id: string }>;
    if (webToken) {
      promise = JWTLoginAPIUtils.getToken(organizationSlug, webToken);
    } else if (invitationToken) {
      promise = JWTLoginAPIUtils.getTokenFromInvitation(organizationSlug, invitationToken);
    } else {
      onFail();
      return;
    }
    // has web token, try to validate the token first
    promise
      .then((response) => {
        if (response.id) {
          const returnUrl = window.location.href;
          cookie.set('returnTo', returnUrl, { expires: 1 });
          redirect.setLocation(`/sso/return/jwt_invite/${response.id}?returnTo=${returnUrl}`);
        }
      })
      .fail(onFail)
      .done();
  }

  changeActiveForm = (
    formKey: SIGNUP_FORM_KEY_TYPE | LOGIN_FORM_KEY_TYPE | COMPLETE_FORM_KEY_TYPE | LINK_FORM_KEY_TYPE
  ) => {
    this.setState(() => ({
      activeForm: formKey,
    }));
  };

  reloadPage = (isNewUser?: boolean) => {
    if (isNewUser) {
      const url = new URL(window.location.href);
      const searchParams = new URLSearchParams();
      searchParams.append('showOnboardingModal', '1');
      searchParams.append('isNewUser', 'true');
      if (isTermsOfUseRolloutForUS()) searchParams.append(SHOW_TERMS_OF_USE_ACCEPT_PARAM, '1');
      url.search = searchParams.toString();
      window.location.replace(url.toString());
    } else {
      window.location.reload();
    }
  };

  renderNav() {
    const { switchFormsByQueryParam } = this.props;
    const { activeForm } = this.state;

    const isSignUp = activeForm === SIGNUP_FORM_KEY;
    const isLogin = activeForm === LOGIN_FORM_KEY;
    const loginLinkClassName = classNames({ active: isLogin });
    const signUpLinkClassName = classNames({ active: isSignUp });

    const FORM_TRANSLATIONS = getFormTranslations();

    if (switchFormsByQueryParam) {
      return (
        <nav>
          <LoginLink
            className={loginLinkClassName}
            selected={isLogin}
            aria-expanded={isLogin}
            aria-controls="authentication-box-content"
            role="button"
          >
            {FORM_TRANSLATIONS.LOGIN}
          </LoginLink>
          <SignupLink
            className={signUpLinkClassName}
            selected={isSignUp}
            aria-expanded={isSignUp}
            aria-controls="authentication-box-content"
            role="button"
          >
            {FORM_TRANSLATIONS.SIGN_UP}
          </SignupLink>
        </nav>
      );
    } else {
      return (
        <nav>
          <TrackedLink2
            trackingName="embedded_login"
            href="#"
            onClick={() => this.changeActiveForm(LOGIN_FORM_KEY)}
            onKeyPress={(event: React.SyntheticEvent<HTMLAnchorElement, Event>) =>
              a11yKeyPress(event, () => this.changeActiveForm(LOGIN_FORM_KEY))
            }
            className={loginLinkClassName}
            aria-selected={isLogin}
            aria-expanded={isLogin}
            aria-controls="authentication-box-content"
            linkType="a"
          >
            {FORM_TRANSLATIONS.LOGIN}
          </TrackedLink2>
          <TrackedLink2
            trackingName="embedded_signup"
            href="#"
            onClick={() => this.changeActiveForm(SIGNUP_FORM_KEY)}
            onKeyPress={(event: React.SyntheticEvent<HTMLAnchorElement, Event>) =>
              a11yKeyPress(event, () => this.changeActiveForm(SIGNUP_FORM_KEY))
            }
            className={signUpLinkClassName}
            aria-selected={isSignUp}
            aria-expanded={isSignUp}
            aria-controls="authentication-box-content"
            linkType="a"
          >
            {FORM_TRANSLATIONS.SIGN_UP}
          </TrackedLink2>
        </nav>
      );
    }
  }

  renderLogo = () => (
    <h1 className="m-a-0 body">
      <CourseraLogo imgSrc={AUTH_MODAL_LOGO_SVG} {...css(styles.courseraRebrandLogoImage)} />
    </h1>
  );

  render() {
    // each form has a different subset of these, but it doesn't hurt to pass them all
    const {
      hideNav,
      hideLogo,
      router,
      customRedirectUrl,
      organizations,
      isEnterprise: isEnterpriseProp,
      overrideFacebookRedirectBlock,
      programId,
      forceSSOLogin,
    } = this.props;
    const { activeForm, useJWT } = this.state;

    const {
      authProvider,
      facebookDisabled,
      completeMode,
      sso,
      isEnterprise: isEnterpriseRouter,
      redirectTo: redirectUrlQueryParam,
      r: alternateRedirectToParam,
      verificationEmailRedirectUrl,
    } = router.location.query;

    const FORM_TRANSLATIONS = getFormTranslations();

    const redirectUrl = redirectUrlQueryParam || alternateRedirectToParam || customRedirectUrl;

    // overrideFacebookRedirectBlock is so on certain pages we can still show the FB buttons even though we want to do a custom redirect after (even though we don't support it through FB)
    // we also don't want to block the FB buttons on the VLP
    const isRedirecting =
      (alternateRedirectToParam || redirectUrl) &&
      !overrideFacebookRedirectBlock &&
      !router.location.pathname.startsWith('/lecture');

    // We don't yet support SSO login on BE when redirectTo/r param is present
    const showSSO = authProvider && !isRedirecting;
    const { isEnterprise, enableSSOLoginV0, enableSSOLoginV1 } = getEnabledVersions({
      authProvider,
      isEnterpriseProp,
      isEnterpriseRouter,
      sso,
    });

    const isRequestFromChina = requestCountryCode === 'CN';
    const isSignUp = activeForm === SIGNUP_FORM_KEY;
    const showGoogle = !(isRequestFromChina || isEnterprise || isRedirecting);
    const showFacebook = !(facebookDisabled || isRequestFromChina || isEnterprise || isRedirecting); // We don't yet support FB login when redirectTo param is present
    const showApple = !(isEnterprise || isRedirecting);
    const isForSaveCourse = router.location.query.saveProduct;

    const formProps = {
      authProvider,
      isEnterprise,
      completeMode,
      redirectUrl,
      verificationEmailRedirectUrl,
      onSuccess: this.reloadPage,
      enableSSOLogin: showSSO,
    };

    // @ts-expect-error TS7016 TODO: modeToForm[activeForm] needs to be typed
    const FormComponent = modeToForm[activeForm] || modeToForm[LOGIN_FORM_KEY];

    // @ts-expect-error ts-migrate(2551) FIXME: Property 'supportsDegreeSsoLogin' does not exist o... Remove this comment to see the full error message
    const hideForm = organizations?.[0]?.supportsDegreeSsoLogin || forceSSOLogin;

    const thirdPartyOrganizationId = organizations?.[0]?.id;
    const whitelistOrganizationProgramsForSSOLogin = EnterpriseExperiments.get(
      'whitelistOrganizationProgramsForSSOLogin'
    );

    const isWhitelistOrganizationProgramsForSSOLogin = thirdPartyOrganizationId
      ? !whitelistOrganizationProgramsForSSOLogin[thirdPartyOrganizationId] ||
        whitelistOrganizationProgramsForSSOLogin[thirdPartyOrganizationId].includes(programId)
      : false;

    const isInvisible = GrowthAcquisitionExperiments.get('useEnterpriseInvisibleReCaptcha');
    const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaLogin');
    const isReCAPTCHAEnterprise = isInvisible || isCheckbox;

    const shouldShowRecaptchaDisclaimer = !isArkoseBotManagerForSignupEnabled() || !isSignUp;

    if (useJWT) return null;

    return (
      <Box
        rootClassName="rc-AuthenticationModal-content m-x-auto"
        style={{ minWidth: 300, maxWidth: 400 }}
        justifyContent="center"
        flexDirection="column"
      >
        <CenterBox rootClassName="w-100" style={{ minWidth: 300, maxWidth: 400 }} tag="header">
          {!hideLogo && this.renderLogo()}
          {isForSaveCourse && <span>{_t('Keep Track of saved items by signing up for free')}</span>}
          {!hideNav && this.renderNav()}
        </CenterBox>
        <div
          {...css('overflow-hidden w-100 m-x-auto', styles.transitionContainer)}
          id="authentication-box-content"
          data-catchpoint="authentication-box-content"
        >
          <Box
            rootClassName="pos-relative p-a-2 bg-white"
            data-e2e="c-user-modal-content"
            style={{
              textAlign: 'left',
              color: color.primaryText,
            }}
            flexDirection="column"
          >
            <ThirdPartyAuthButtons
              // @ts-expect-error TS7016 TODO: onSuccess has inCompatible types
              onSuccess={this.reloadPageWithOnboardingModal}
              showGoogle={showGoogle}
              showFacebook={showFacebook}
              showApple={showApple}
              authProvider={authProvider}
            />
            {authProvider && isWhitelistOrganizationProgramsForSSOLogin && (
              <SsoButton organizations={organizations} provider={authProvider} showFooter={!forceSSOLogin} />
            )}
            {!hideForm && enableSSOLoginV0 && <FormComponent {...formProps} />}
            {enableSSOLoginV1 && (
              <AuthenticationModalContentV1
                tag={FormComponent}
                formProps={formProps}
                isSignUp={isSignUp}
                disableSignupLoginWithOrgButton={isRedirecting}
              />
            )}
            {!forceSSOLogin && (
              <div className="m-t-1s caption-text text-xs-center">
                {shouldShowRecaptchaDisclaimer && (
                  <div className="m-b-1">
                    <FormattedMessage
                      message={
                        isReCAPTCHAEnterprise
                          ? _t(
                              'This site is protected by reCAPTCHA Enterprise and the Google {privacyPolicyLink} and {tosLink} apply.'
                            )
                          : _t(
                              'This site is protected by reCAPTCHA and the Google {privacyPolicyLink} and {tosLink} apply.'
                            )
                      }
                      privacyPolicyLink={
                        <TrackedA
                          trackingName="auth_modal_privacy_policy_link"
                          href="https://policies.google.com/privacy"
                        >
                          {_t('Privacy Policy')}
                        </TrackedA>
                      }
                      tosLink={
                        <TrackedA trackingName="auth_modal_tos_link" href="https://policies.google.com/terms">
                          {_t('Terms of Service')}
                        </TrackedA>
                      }
                    />
                  </div>
                )}
                <TrackedA
                  href="https://learner.coursera.help/hc/articles/209818553"
                  target="_blank"
                  rel="noopener noreferrer"
                  trackingName="auth_modal_having_trouble_logging_in_link"
                >
                  {FORM_TRANSLATIONS.HAVING_TROUBLE}
                  <SvgExternalLink
                    size={14}
                    color={color.primaryText}
                    style={{ marginBottom: '-4px', marginLeft: '4px' }}
                  />
                </TrackedA>
              </div>
            )}
          </Box>
        </div>
      </Box>
    );
  }
}

export const ThirdPartyOrganizationsQuery = gql`
  query UserThirdPartyOrganizationsQuery($authProviderSlug: String!) {
    ThirdPartyOrganizationsV1(slug: $authProviderSlug)
      @rest(
        type: "ThirdPartyOrganizationsV1"
        path: "thirdPartyOrganizations.v1?q=bySlug&slug={args.slug}&fields=name,supportsDegreeSSOLogin"
        method: "GET"
      ) {
      elements @type(name: "ThirdPartyOrganization") {
        id
        name
        supportsDegreeSsoLogin
      }
    }
  }
`;

type ThirdPartyOrgsGraphqlResponse = {
  data?: { ThirdPartyOrganizationsV1: { elements: Array<ThirdPartyOrganizationsV1> } };
};

export default compose<Props, Omit<Props, 'router' | 'organizations'>>(
  connectToRouter((router) => ({ router })),
  withProps(({ router }: Props) => ({
    authProviderSlug: router.location.query.authProvider,
  })),
  graphql<ThirdPartyOrgsGraphqlResponse, {}, {}, ThirdPartyOrgsGraphqlData>(ThirdPartyOrganizationsQuery, {
    skip: ({ authProviderSlug }: any) => !authProviderSlug,
    props: ({ data }: OptionProps<ThirdPartyOrgsGraphqlResponse>) => {
      return {
        organizations: data?.ThirdPartyOrganizationsV1?.elements || [],
      };
    },
  }),
  graphql<ThirdPartyOrgsGraphqlData, ForceSsoLoginQuery, ForceSsoLoginQueryVariables, PropsFromForceSsoLoginQuery>(
    ForceSSOLogin,
    {
      skip: ({ organizations }) => !organizations?.[0]?.id,
      options: ({ organizations }) => ({
        context: { clientName: 'gatewayGql' },
        // We check in the skip above that the id is defined
        variables: { orgId: organizations?.[0]?.id },
      }),
      props: ({ data }) => {
        return { forceSSOLogin: Boolean(data?.EntSso?.queryIfSsoForceLoginByOrgId?.isSsoLoginForced) };
      },
    }
  )
)(AuthenticationFormsContainer);
